/**
 * C.11 — `[window-context]` block builder.
 *
 * Prepended to every user message so NIPE can reason about what the
 * user is currently focused on. Read from `WindowContext.windows`,
 * filtered to `state === 'open'`, sorted by `zIndex` desc, head.
 *
 * Per `feedback_skills_no_client_data` the focused-note path is the
 * vault-relative path string (e.g. "Cases/Hadley v. Baxendale.md") —
 * not a client-specific URL. Portal slug is the client's identifier
 * but is intentionally included so NIPE can mention "your areweai
 * portal" rather than a generic "the portal".
 */

export interface WindowSnapshot {
  path: string;
  title: string;
  state: 'open' | 'closed' | 'minimized';
  zIndex: number;
}

export interface WindowContextInput {
  windows: ReadonlyArray<WindowSnapshot>;
  portalSlug: string | null;
  isDemo: boolean;
  focusedNotePath?: string | null;
}

/** Returns the open window with the highest zIndex, or null if none. */
export function pickFocusedWindow(
  windows: ReadonlyArray<WindowSnapshot>,
): WindowSnapshot | null {
  let best: WindowSnapshot | null = null;
  for (const w of windows) {
    if (w.state !== 'open') continue;
    if (!best || w.zIndex > best.zIndex) best = w;
  }
  return best;
}

export function buildWindowContextBlock(input: WindowContextInput): string {
  const focused = pickFocusedWindow(input.windows);
  const lines = ['[window-context]'];
  lines.push(`focused: ${focused?.title ?? '(none)'}`);
  if (input.focusedNotePath) lines.push(`note: ${input.focusedNotePath}`);
  lines.push(`portal: ${input.portalSlug ?? '(none)'}`);
  lines.push(`mode: ${input.isDemo ? 'demo' : 'live'}`);
  // F-NIPE-DESKTOP-AWARENESS — list every non-closed window so the
  // model knows what else is reachable without calling listOpenWindows.
  // Closed entries are noise; minimized entries are useful because the
  // model may want to focus them.
  const visible = input.windows.filter((w) => w.state !== 'closed');
  if (visible.length > 0) {
    const summary = visible
      .map((w) => (w.state === 'minimized' ? `${w.title} (min)` : w.title))
      .join(', ');
    lines.push(`open: ${summary}`);
  }
  lines.push('[/window-context]');
  return lines.join('\n');
}
